import React, { useState, useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

import { OrderListItem } from './OrderListItem';
import { OrderListDriverBreakItem } from './OrderListDriverBreakItem';
import { getRouteColor, routeOrdersToStages, stagesToRouteOrders, getNewStageIndex, RouteElementType } from '../../utils/utils';

export const SortableOrderList = ({ onClick, onFocusClick, onDragEnd, routeOrders, formRouteOrders, maxRouteOrders, onRemoveDriverBreak, routeId }) => {
  const [startIndex, setStartIndex] = useState(null);
  const stages = useMemo(() => routeOrdersToStages(routeOrders), [routeOrders]);

  const handleDragStart = (start) => {
    setStartIndex(start.source.droppableId);
  };

  const handleDragEnd = (results) => {
    setStartIndex(null);
    const { destination, source } = results;
    const order = routeOrders.find((element) => {
      return (element.order?.trackingId || element.uuid) === results.draggableId;
    });

    const newStages = { ...stages };

    if (!destination) return;

    const destinationId =
      destination.droppableId === 'new-stage' ? getNewStageIndex(stages) : parseInt(destination.droppableId.substring(12));
    const sourceId = parseInt(source.droppableId.substring(12));
    const newDestination = newStages[destinationId] ? Array.from(newStages[destinationId]) : [];

    if (source.droppableId === destination.droppableId) {
      newDestination.splice(source.index, 1);
      newDestination.splice(destination.index, 0, order);
      newStages[destinationId] = newDestination;

      return onDragEnd(stagesToRouteOrders(newStages));
    }

    const newSource = Array.from(newStages[sourceId]);

    newSource.splice(source.index, 1);
    newStages[sourceId] = newSource;

    newDestination.splice(destination.index, 0, order);
    newStages[destinationId] = newDestination;

    if (newStages[sourceId].length === 0) {
      delete newStages[sourceId];
    }

    onDragEnd(stagesToRouteOrders(newStages));
  };

  if (!stages) return;

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {Object.keys(stages).map((stageIndex) => (
        <div className="stage-container" key={stageIndex} style={{ borderColor: getRouteColor(stageIndex) }}>
          <div className="stage-container__background" style={{ backgroundColor: getRouteColor(stageIndex) }} />
          <Droppable
            droppableId={`droppableId-${stageIndex}`}
            isDropDisabled={stages[stageIndex].length >= maxRouteOrders && startIndex !== `droppableId-${stageIndex}`}
          >
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {stages[stageIndex].map((element, index) => {
                  const formRouteOrder = formRouteOrders.find(routeOrder => routeOrder.orderId === element?.order?.id);

                  const bufferTime = formRouteOrder?.routeBufferStop?.humanFormat;
                  const order = element?.order;
                  const breakIndex = (stages[stageIndex].filter((element) => element.type === RouteElementType.DRIVER_BREAK_TIME)?.length ?? 0) + 1;

                  return (
                    <React.Fragment key={order?.id || element.uuid}>
                      <Draggable draggableId={order?.trackingId || element.uuid} index={index}>
                        {(provided) => (
                          <div {...provided.draggableProps} {...provided.dragHandleProps} ref={provided.innerRef} key={order?.trackingId || element.uuid}>
                            {element.type === RouteElementType.ROUTE_ORDER && (
                              <OrderListItem
                                key={order?.trackingId || element.uuid}
                                order={{ ...order, buffer: bufferTime }}
                                selected={true}
                                calculatedArrivalTime={formRouteOrder?.calculatedArrivalTime}
                                hideStatus={true}
                                onClick={() => onClick(order || element)}
                                onFocusClick={() => onFocusClick(order)}
                                showOrderColor
                                routeId={routeId}
                              />
                            )}

                            {element.type === RouteElementType.DRIVER_BREAK_TIME && (
                              <OrderListDriverBreakItem
                                key={order?.trackingId || element.uuid}
                                element={element}
                                routeOrders={routeOrders}
                                onRemoveDriverBreak={onRemoveDriverBreak}
                              />
                            )}
                          </div>
                        )}
                      </Draggable>
                    </React.Fragment>
                  )})}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      ))}

      {Object.keys(stages).length > 0 && (
        <div className="stage-container" style={{ borderColor: getRouteColor(getNewStageIndex(stages)) }}>
          <div className="stage-container__background" style={{ backgroundColor: getRouteColor(getNewStageIndex(stages)) }} />
          <Droppable droppableId={`new-stage`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="text-center">
                {provided.placeholder}
                <strong style={{ color: getRouteColor(getNewStageIndex(stages)) }}>
                  {__('routes.new_stage_placeholder')}
                </strong>
              </div>
            )}
          </Droppable>
        </div>
      )}
    </DragDropContext>
  );
};
